/**
 * Care Gaps V2 — service layer
 *
 * Apunta a la nueva base DB_MCM_SANTEPLUS (esquema clinical) a través del
 * controlador `CareGapsV2Controller`. JWT obligatorio en todas las llamadas
 * — el backend lee el employeeId y la IP del token/conexión para auditoría
 * HIPAA, el front no necesita enviarlos.
 *
 * Referencia: docs/modulos/care-gaps/CaregapsV2/CAREGAPS_V2_ENDPOINTS.md
 */
import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/CareGapsV2`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const buildParams = (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") params[k] = v;
  });
  return params;
};

const unwrap = (response, fallback) => {
  if (response.status === 204) return fallback;
  return response.data ?? fallback;
};

/**
 * Lista paginada de care gaps (vista principal del worklist).
 * Backend: `clinical.SP_GET_CARE_GAPS`.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.measurementYear]   - default: año más reciente en BD
 * @param {number|null} [filters.careGapStatusId]   - 53=open, 54=submitted, 55=closed
 * @param {number|null} [filters.workflowStatusId]  - 56..86
 * @param {string|null} [filters.careGapName]       - nombre exacto de la medida
 * @param {string|null} [filters.cipId]             - paciente específico
 * @param {string|null} [filters.patientName]       - LIKE %nombre%
 * @param {string|null} [filters.dateOfBirth]       - YYYY-MM-DD
 * @param {number|null} [filters.pcpEmployeeId]
 * @param {number|null} [filters.locationId]
 * @param {number} [filters.page=1]
 * @param {number} [filters.pageSize=50]            - máx 200
 *
 * Ejemplo de llamada:
 * GET /api/CareGapsV2?careGapStatusId=53&workflowStatusId=56&page=1&pageSize=50
 *
 * Respuesta exitosa (200):
 * {
 *   "data": [
 *     {
 *       "careGapId": 1234,
 *       "cipId": "18750327012",
 *       "memberId": "002W24287",
 *       "careGapName": "Breast Cancer Screening",
 *       "measurementYear": 2026,
 *       "careGapStatusId": 53, "careGapStatusName": "Open", "careGapStatusKey": "open",
 *       "workflowStatusId": 56, "workflowStatusName": "New - Not reviewed", "workflowStatusKey": "new",
 *       "workflowUpdated": null,
 *       "submittedDate": null, "submittedBy": "", "serviceDateOfClosure": null,
 *       "historicallyNonCompliant": true,
 *       "refreshWeek": 20260508, "lastSeen": "2026-05-08T06:00:00",
 *       "measureCode": "BSC", "hedisDisplayName": "Breast Cancer Screening",
 *       "hedisCategory": "cancer_screening", "contractual": true, "qmCode": "QM1",
 *       "encounterType": "1", "inverted": false,
 *       "patientFirstName": "MARGUERITE", "patientLastName": "MICHEL",
 *       "patientFullName": "MICHEL, MARGUERITE MARIANA",
 *       "patientDob": "1965-03-15T00:00:00", "patientAge": 61, "patientGender": "F",
 *       "patientPhone": "(954) 555-1234", "patientMobile": "(954) 555-5678", "patientEmail": "marguerite@example.com",
 *       "pcpEmployeeId": 12, "pcpFirstName": "YANELA", "pcpLastName": "MOLINA-CHUY", "pcpFullName": "YANELA MOLINA-CHUY",
 *       "locationId": 1, "locationName": "North", "locationKey": "north", "officeLocation": "Sante Plus North"
 *     }
 *   ],
 *   "page": 1,
 *   "pageSize": 50
 * }
 *
 * Códigos:
 * - 200 OK
 * - 204 No Content     → consulta válida sin resultados (se devuelve { data: [], page, pageSize })
 * - 401 Unauthorized   → JWT faltante o expirado
 */
export const getCareGaps = async (filters = {}) => {
  try {
    const response = await axios.get(API_URL, {
      headers: authHeaders(),
      params: buildParams(filters),
    });
    return unwrap(response, { data: [], page: filters.page ?? 1, pageSize: filters.pageSize ?? 50 });
  } catch (error) {
    console.error("Error en getCareGaps:", error);
    throw error;
  }
};

/**
 * Conteo total de care gaps para los mismos filtros del listado.
 * Backend: `clinical.SP_COUNT_CARE_GAPS`. Llamar **antes** de getCareGaps para
 * conocer el total de páginas.
 *
 * @param {Object} [filters] - mismos campos que `getCareGaps` excepto `page`.
 *
 * Ejemplo:
 * GET /api/CareGapsV2/count?careGapStatusId=53
 *
 * Respuesta exitosa (200):
 * { "totalRecords": 13508, "totalPages": 271, "pageSize": 50, "measurementYear": 2026 }
 */
export const getCareGapsCount = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/count`, {
      headers: authHeaders(),
      params: buildParams(filters),
    });
    return unwrap(response, { totalRecords: 0, totalPages: 0, pageSize: filters.pageSize ?? 50, measurementYear: null });
  } catch (error) {
    console.error("Error en getCareGapsCount:", error);
    throw error;
  }
};

/**
 * Pacientes con conteos agregados de care gaps (vista By Patient).
 * Backend: `clinical.SP_GET_CARE_GAPS_SUMMARY_BY_PATIENT`. Usa la tabla
 * pre-calculada por el ETL diario, por lo que es muy rápido.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.measurementYear]
 * @param {number|null} [filters.pcpEmployeeId]
 * @param {number|null} [filters.locationId]
 * @param {string|null} [filters.patientName]   - LIKE %nombre%
 * @param {number} [filters.page=1]
 * @param {number} [filters.pageSize=50]
 *
 * Ejemplo:
 * GET /api/CareGapsV2/by-patient?locationId=2&page=1&pageSize=20
 *
 * Respuesta exitosa (200):
 * {
 *   "data": [
 *     {
 *       "cipId": "13750421596",
 *       "measurementYear": 2026,
 *       "total": 11, "open": 8, "submitted": 1, "closed": 2,
 *       "notReviewed": 6, "inProgress": 2, "historicallyNonCompliant": 3,
 *       "lastWorkflowUpdate": "2026-05-09T14:32:00",
 *       "lastSeen": "2026-05-08T06:00:00",
 *       "summaryUpdated": "2026-05-12T03:00:00",
 *       "patientFirstName": "EMANE", "patientLastName": "AUGUSTIN",
 *       "patientFullName": "AUGUSTIN, EMANE",
 *       "patientDob": "1958-06-12T00:00:00", "patientAge": 67, "patientGender": "M",
 *       "patientPhone": "", "patientMobile": "(954) 123-4567",
 *       "pcpEmployeeId": 8, "pcpFullName": "AWAIS SIDDIKI",
 *       "locationId": 2, "locationName": "South", "locationKey": "south"
 *     }
 *   ],
 *   "pagination": { "totalPatients": 5641, "totalPages": 113, "currentPage": 1, "pageSize": 50, "measurementYear": 2026 }
 * }
 *
 * 204 No Content si el ETL no ha corrido para el año (tabla de resumen vacía).
 */
export const getCareGapsByPatient = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/by-patient`, {
      headers: authHeaders(),
      params: buildParams(filters),
    });
    return unwrap(response, {
      data: [],
      pagination: { totalPatients: 0, totalPages: 0, currentPage: filters.page ?? 1, pageSize: filters.pageSize ?? 50, measurementYear: null },
    });
  } catch (error) {
    console.error("Error en getCareGapsByPatient:", error);
    throw error;
  }
};

/**
 * Agregado por tipo de medida (open/submitted/closed por medida) para el
 * dashboard de barras. Backend: `clinical.SP_GET_CARE_GAP_TYPE_SUMMARY`.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.measurementYear]
 * @param {number|null} [filters.locationId]
 * @param {number|null} [filters.pcpEmployeeId]
 * @param {boolean} [filters.includeAllRow=false] - agrega fila "All" al final
 *
 * Ejemplo:
 * GET /api/CareGapsV2/type-summary?includeAllRow=true
 *
 * Respuesta exitosa (200):
 * [
 *   { "careGapType": "Controlling High Blood Pressure", "open": 521, "closed": 587, "submitted": 23, "total": 1131 },
 *   { "careGapType": "Breast Cancer Screening",         "open": 345, "closed": 764, "submitted": 12, "total": 1121 },
 *   { "careGapType": "All",                              "open": 7079, "closed": 11164, "submitted": 344, "total": 18587 }
 * ]
 */
export const getCareGapTypeSummary = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/type-summary`, {
      headers: authHeaders(),
      params: buildParams(filters),
    });
    return unwrap(response, []);
  } catch (error) {
    console.error("Error en getCareGapTypeSummary:", error);
    throw error;
  }
};

/**
 * Reporte HEDIS de rendimiento real-time con peer rates BCBS.
 * Backend: `clinical.SP_GET_HEDIS_PERFORMANCE` (devuelve dos result sets:
 * measures + summary).
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.measurementYear]
 * @param {number|null} [filters.locationId]        - NULL = toda la organización
 * @param {boolean} [filters.contractualOnly=false] - true = solo los 7 QMs contractuales
 *
 * Ejemplo:
 * GET /api/CareGapsV2/hedis-performance?contractualOnly=true
 *
 * Respuesta exitosa (200):
 * {
 *   "measures": [
 *     {
 *       "hedisMeasureId": 5, "measureCode": "CBP",
 *       "habilityName": "Controlling High Blood Pressure",
 *       "bcbsName": "Controlling High Blood Pressure",
 *       "category": "chronic_disease", "encounterType": "1",
 *       "contractual": true, "qmCode": "QM6", "inverted": false,
 *       "peerRate": 0.569005, "peerRateYear": 2026, "peerRateMonth": 5,
 *       "total": 876, "open": 178, "submitted": 0, "closed": 698,
 *       "notReviewed": 12, "inProgress": 165,
 *       "internalRate": 0.796804,
 *       "passingPeerRate": 1,    // 1=sí | 0=no | -1=sin peer rate
 *       "gapToPeerRate": -200    // negativo=superado | null=sin peer rate o invertida
 *     }
 *   ],
 *   "summary": {
 *     "measurementYear": 2026, "locationId": 0,
 *     "totalOpportunities": 13508, "totalOpen": 9710, "totalSubmitted": 94, "totalClosed": 3704,
 *     "totalNotReviewed": 5012, "globalRate": 0.274200,
 *     "qmsPassing": 4, "qmsWithPeerRate": 7,
 *     "totalMeasures": 65, "activeMeasures": 58
 *   }
 * }
 */
export const getHedisPerformance = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/hedis-performance`, {
      headers: authHeaders(),
      params: buildParams(filters),
    });
    return unwrap(response, { measures: [], summary: null });
  } catch (error) {
    console.error("Error en getHedisPerformance:", error);
    throw error;
  }
};

/**
 * Detalle completo de un care gap específico.
 * Backend: `clinical.SP_GET_CARE_GAP_DETAIL`.
 *
 * @param {number} careGapId - ID del care gap
 *
 * Ejemplo:
 * GET /api/CareGapsV2/1234
 *
 * Respuesta exitosa (200): mismo shape que un item de getCareGaps + peerRate
 * y firstUploadId / lastUploadId / createDate / updateDate.
 *
 * 404 Not Found si el ID no existe.
 */
export const getCareGapDetail = async (careGapId) => {
  try {
    const response = await axios.get(`${API_URL}/${careGapId}`, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error("Error en getCareGapDetail:", error);
    throw error;
  }
};

/**
 * Timeline unificado del care gap (workflow + status_history).
 * Backend: `clinical.SP_GET_CARE_GAP_HISTORY`. Ordenado por eventDate DESC.
 *
 * @param {number} careGapId
 *
 * Ejemplo:
 * GET /api/CareGapsV2/1234/history
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "historyId": 45,
 *     "historyType": "workflow",      // 'workflow' | 'status'
 *     "actionType": "outreach",        // 'workflow_change' | 'schedule' | 'outreach' | 'submit' | 'status_change'
 *     "eventDate": "2026-05-11T10:23:00",
 *     "workflowStatusFromId": 58, "workflowStatusFrom": "In progress",
 *     "workflowStatusToId": 62,   "workflowStatusTo": "Contacted",
 *     "statusFrom": "", "statusTo": "",
 *     "outreachChannel": "Phone", "outreachResult": "Contacted", "outreachNextAction": "Schedule appointment",
 *     "scheduledDate": null, "locationScheduled": "",
 *     "submitTitle": "", "submitNote": "",
 *     "note": "Called patient. Will schedule mammogram next week.",
 *     "employeeId": 5, "employeeFullName": "Maria Rodriguez",
 *     "createDate": "2026-05-11T10:23:00"
 *   }
 * ]
 *
 * `employeeId = 0` y `employeeFullName = "System (Hability Sync)"` para eventos del ETL.
 */
export const getCareGapHistory = async (careGapId) => {
  try {
    const response = await axios.get(`${API_URL}/${careGapId}/history`, { headers: authHeaders() });
    return unwrap(response, []);
  } catch (error) {
    console.error("Error en getCareGapHistory:", error);
    throw error;
  }
};

/**
 * Catálogo de workflow statuses de los care gaps, agrupados y ordenados.
 * Alimenta el dropdown del modal "Care Gap Actions" (cambio de workflow).
 *
 * Ejemplo de llamada:
 * GET /api/CareGapsV2/workflow-statuses
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "statusId": 56,
 *     "statusKey": "new",
 *     "statusName": "New - Not reviewed",
 *     "groupName": "Opening",
 *     "groupOrder": 1,
 *     "displayOrder": 1
 *   },
 *   {
 *     "statusId": 59,
 *     "statusKey": "contact_attempted",
 *     "statusName": "Contact attempted",
 *     "groupName": "Contact",
 *     "groupOrder": 2,
 *     "displayOrder": 4
 *   }
 * ]
 *
 * Códigos:
 * - 200 OK
 * - 204 No Content    → catálogo vacío (se devuelve [])
 * - 401 Unauthorized  → JWT faltante o expirado
 */
export const getWorkflowStatuses = async () => {
  try {
    const response = await axios.get(`${API_URL}/workflow-statuses`, { headers: authHeaders() });
    return unwrap(response, []);
  } catch (error) {
    console.error("Error en getWorkflowStatuses:", error);
    throw error;
  }
};

/**
 * Cambia el workflow status de un care gap.
 * Backend: `clinical.SP_SAVE_CARE_GAP_WORKFLOW`.
 *
 * @param {number} careGapId
 * @param {Object} body
 * @param {number} body.workflowStatusToId - nuevo workflow (56..86)
 * @param {string} [body.note]
 *
 * Body ejemplo:
 * { "workflowStatusToId": 58, "note": "Reviewing patient history." }
 *
 * Respuestas:
 * - 200 { "result": 0, "message": "Workflow status updated successfully." }
 * - 400 { "result": 1, "message": "Care gap not found." }
 * - 400 { "result": 2, "message": "New workflow status is the same as current." }
 * - 400 { "result": 3, "message": "Invalid workflow status ID." }
 */
export const saveCareGapWorkflow = async (careGapId, body) => {
  try {
    const response = await axios.post(`${API_URL}/${careGapId}/workflow`, body, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error("Error en saveCareGapWorkflow:", error);
    throw error;
  }
};

/**
 * Agenda una cita asociada al care gap. Mueve el workflow a 65 (Scheduled)
 * salvo que ya esté entre 67-75 (estados más avanzados).
 * Backend: `clinical.SP_SAVE_CARE_GAP_SCHEDULE`.
 *
 * @param {number} careGapId
 * @param {Object} body
 * @param {string} body.scheduledDate - YYYY-MM-DD
 * @param {number} [body.locationId]
 * @param {string} [body.note]
 *
 * Body ejemplo:
 * { "scheduledDate": "2026-05-20", "locationId": 1, "note": "Mammogram scheduled with Dr. Smith." }
 *
 * Respuestas:
 * - 200 { "result": 0, "message": "Schedule registered successfully." }
 * - 400 { "result": 1, "message": "Care gap not found." }
 */
export const saveCareGapSchedule = async (careGapId, body) => {
  try {
    const response = await axios.post(`${API_URL}/${careGapId}/schedule`, body, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error("Error en saveCareGapSchedule:", error);
    throw error;
  }
};

/**
 * Registra un intento o contacto exitoso de outreach.
 * Backend: `clinical.SP_SAVE_CARE_GAP_OUTREACH`.
 *
 * @param {number} careGapId
 * @param {Object} body
 * @param {number} body.workflowStatusToId   - workflow resultante (59=contact_attempted, 62=contacted, 63=refused, 60=no_phone, ...)
 * @param {string} body.outreachChannel      - 'Phone' | 'Text' | 'Email' | 'In person'
 * @param {string} body.outreachResult
 * @param {string} [body.outreachNextAction]
 * @param {string} [body.note]
 *
 * Body ejemplo:
 * {
 *   "workflowStatusToId": 62,
 *   "outreachChannel": "Phone",
 *   "outreachResult": "Contacted",
 *   "outreachNextAction": "Schedule appointment",
 *   "note": "Patient agreed to schedule mammogram."
 * }
 *
 * Respuestas:
 * - 200 { "result": 0, "message": "Outreach registered successfully." }
 * - 400 { "result": 1, "message": "Care gap not found." }
 * - 400 { "result": 3, "message": "Invalid workflow status ID." }
 */
export const saveCareGapOutreach = async (careGapId, body) => {
  try {
    const response = await axios.post(`${API_URL}/${careGapId}/outreach`, body, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error("Error en saveCareGapOutreach:", error);
    throw error;
  }
};

/**
 * Registra el submit a Availity. Mueve el workflow a 72 (Closed on Availity).
 * El Care Gap Status (53/54/55) lo actualizará el sync diario de Hability.
 * Backend: `clinical.SP_SAVE_CARE_GAP_SUBMIT`.
 *
 * @param {number} careGapId
 * @param {Object} body
 * @param {string} body.submitTitle
 * @param {string} [body.submitNote]
 * @param {string} [body.note]
 *
 * Body ejemplo:
 * {
 *   "submitTitle": "Breast Cancer Screening - Evidence",
 *   "submitNote": "Mammogram results from 03/15/2026 attached.",
 *   "note": "Submitted via Availity."
 * }
 *
 * Respuestas:
 * - 200 { "result": 0, "message": "Submit to Availity registered successfully." }
 * - 400 { "result": 1, "message": "Care gap not found." }
 */
export const saveCareGapSubmit = async (careGapId, body) => {
  try {
    const response = await axios.post(`${API_URL}/${careGapId}/submit`, body, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error("Error en saveCareGapSubmit:", error);
    throw error;
  }
};

/**
 * Actualiza los peer rates de los 7 QMs contractuales cuando llega el reporte
 * mensual de BCBS Florida.
 * Backend: `clinical.SP_UPDATE_HEDIS_PEER_RATES`. Solo se actualizan los QMs
 * cuyo campo se envía en el body.
 *
 * @param {Object} body
 * @param {number} body.reportYear     - >= 2025
 * @param {number} body.reportMonth    - 1..12
 * @param {number} [body.rateQm1]      - 0..1 (decimal)
 * @param {number} [body.rateQm2]
 * @param {number} [body.rateQm3]
 * @param {number} [body.rateQm4]
 * @param {number} [body.rateQm5]
 * @param {number} [body.rateQm6]
 * @param {number} [body.rateQm7]
 *
 * Body ejemplo:
 * { "reportYear": 2026, "reportMonth": 5, "rateQm1": 0.755720, "rateQm6": 0.569005 }
 *
 * Respuestas:
 * - 200 { "result": 0, "message": "Peer rates updated successfully. Records: 7 | Report: 2026/5" }
 * - 400 { "result": 1, "message": "Invalid report year. Must be >= 2025." }
 * - 400 { "result": 2, "message": "Invalid report month. Must be 1-12." }
 * - 400 { "result": 3, "message": "Rate values must be between 0 and 1." }
 */
export const updateHedisPeerRates = async (body) => {
  try {
    const response = await axios.put(`${API_URL}/hedis-peer-rates`, body, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error("Error en updateHedisPeerRates:", error);
    throw error;
  }
};
